import type { GalleryImage } from '../types/gallery.types.js';
import { buildSrcset, type Image } from './image.utils.js';

export interface ImageRenderData {
    src: string;
    srcset: string;
    alt: string;
    /** Optional aspect ratio propagated from GalleryImage (e.g. '3/2', '4/3', '16/9'). */
    aspectRatio?: string;
}

export interface RightImageRenderData extends ImageRenderData {
    loading: 'eager' | 'lazy';
}

export interface GalleryRenderDataPlaceholder {
    placeholder: true;
}

export type RightSlotRenderData = RightImageRenderData | GalleryRenderDataPlaceholder;

export interface GalleryRenderData {
    left: ImageRenderData;
    right: RightSlotRenderData[];
    /** Anzahl der echten Bilder (für Mobile: bei < 3 nur ein Bild anzeigen) */
    imageCount: number;
}

function parseAspectRatioValue(aspectRatio?: string): number | undefined {
    if (!aspectRatio) return undefined;

    if (aspectRatio.includes('/')) {
        const [width, height] = aspectRatio.split('/').map((value) => Number(value));
        if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
            return width / height;
        }
        return undefined;
    }

    const numericRatio = Number(aspectRatio);
    return Number.isFinite(numericRatio) && numericRatio > 0 ? numericRatio : undefined;
}

function normalizeGroupUrl(url: string): string {
    if (!url) return '';

    const sizeSegmentPattern = /\/(ORIGINAL|DESKTOP|TABLET|MOBILE)\//g;
    try {
        const parsed = new URL(url);
        return `${parsed.origin}${parsed.pathname.replace(sizeSegmentPattern, '/__SIZE__/')}`;
    } catch {
        return url.replace(sizeSegmentPattern, '/__SIZE__/');
    }
}

function getImageGroupKey(image: GalleryImage): string {
    if ((!image.text?.trim() && !image.title?.trim()) || (image.title === 'None' && image.text === 'None')) {
        return image.url.split('/').pop()?.split('.').shift()?.toString() ?? '';
    }
    if (image.title?.trim()) return image.title;
    if (image.text?.trim()) return image.text;
    return normalizeGroupUrl(image.url);
}

function groupImagesByTitle(images: GalleryImage[]): Map<string, GalleryImage[]> {
    const grouped = new Map<string, GalleryImage[]>();
    for (const image of images) {
        const key = getImageGroupKey(image);
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(image);
    }
    return grouped;
}

function pickAspectRatio(images: GalleryImage[]): string | undefined {
    return images.find((image) => parseAspectRatioValue(image.aspectRatio) !== undefined)?.aspectRatio;
}

export interface PreloadAspectRatioOptions {
    preferredSize?: GalleryImage['size'];
}

export interface SortGalleryImagesOptions {
    maxSortedGroups?: number;
    sortMode?: 'ratio' | 'landscape';
}

/**
 * Reuses existing aspect ratio metadata per image group without triggering extra image requests.
 * If backend does not provide aspectRatio, callers fall back to neutral ratio-based ordering.
 * @param images - Flat gallery image list with variants per logical image
 * @param options - Kept for API compatibility; no network preload is performed
 * @returns Copy of images with propagated aspectRatio per image group
 */
export async function preloadAspectRatios(
    images: GalleryImage[] | undefined,
    options: PreloadAspectRatioOptions = {}
): Promise<GalleryImage[]> {
    if (!images?.length) return [];

    void options;
    const enrichedImages = images.map((image) => ({ ...image }));
    const grouped = groupImagesByTitle(enrichedImages);

    for (const group of grouped.values()) {
        if (!group.length) continue;

        const existingRatio = pickAspectRatio(group);
        if (!existingRatio) continue;

        for (const image of group) {
            image.aspectRatio = existingRatio;
        }
    }

    return enrichedImages;
}

/**
 * Sorts image groups with stable ordering.
 * - ratio mode: descending by computed aspect ratio
 * - landscape mode: landscape groups first (ratio > 1), then original order
 * @param images - Flat gallery image list with variants per logical image
 * @param options - Optional sorting constraints (e.g. sort only first N image groups)
 * @returns Sorted flat list where each image group keeps its internal variant order
 */
export function sortGalleryImagesByAspectRatio(
    images: GalleryImage[] | undefined,
    options: SortGalleryImagesOptions = {}
): GalleryImage[] {
    if (!images?.length) return [];

    const grouped = groupImagesByTitle(images);
    const firstIndexByKey = new Map<string, number>();
    const maxSortedGroups = options.maxSortedGroups;
    const sortMode = options.sortMode ?? 'ratio';

    images.forEach((image, index) => {
        const key = getImageGroupKey(image);
        if (!firstIndexByKey.has(key)) {
            firstIndexByKey.set(key, index);
        }
    });

    const allGroups = Array.from(grouped.entries());
    const shouldLimit = typeof maxSortedGroups === 'number' && maxSortedGroups > 0;
    const sortLimit = shouldLimit ? Math.min(maxSortedGroups, allGroups.length) : allGroups.length;

    const ratioByKey = new Map<string, number>();
    for (const [key, group] of allGroups) {
        ratioByKey.set(key, parseAspectRatioValue(pickAspectRatio(group)) ?? 1);
    }

    const isLandscapeByKey = new Map<string, boolean>();
    for (const [key] of allGroups) {
        isLandscapeByKey.set(key, (ratioByKey.get(key) ?? 1) > 1);
    }

    const ratioSortedGroups = [...allGroups].sort(([keyA], [keyB]) => {
        if (sortMode === 'landscape') {
            const landscapeA = isLandscapeByKey.get(keyA) ?? false;
            const landscapeB = isLandscapeByKey.get(keyB) ?? false;
            if (landscapeA !== landscapeB) {
                return landscapeB ? 1 : -1;
            }

            return (firstIndexByKey.get(keyA) ?? 0) - (firstIndexByKey.get(keyB) ?? 0);
        }

        const ratioA = ratioByKey.get(keyA) ?? 1;
        const ratioB = ratioByKey.get(keyB) ?? 1;
        if (ratioA !== ratioB) {
            return ratioB - ratioA;
        }

        return (firstIndexByKey.get(keyA) ?? 0) - (firstIndexByKey.get(keyB) ?? 0);
    });

    if (!shouldLimit) {
        return ratioSortedGroups.flatMap(([, group]) => group);
    }

    const promotedGroups = ratioSortedGroups.slice(0, sortLimit);
    const promotedKeys = new Set(promotedGroups.map(([key]) => key));
    const untouchedGroups = allGroups.filter(([key]) => !promotedKeys.has(key));

    const sortedGroups = [...promotedGroups, ...untouchedGroups];
    return sortedGroups.flatMap(([, group]) => group);
}

/**
 * Converts GalleryImage[] to Image[] for srcset building.
 * @param galleryImages - Array of GalleryImage objects to convert
 * @returns Array of Image objects with size and url
 */
function toImageArray(galleryImages: GalleryImage[]): Image[] {
    return galleryImages.map((img) => ({ size: img.size, url: img.url }));
}

/**
 * Pick overlay URL from group if set, else fallback to normal src.
 * @param images - Bildergruppe, aus der die optionale Overlay-URL gelesen wird
 * @returns Overlay-URL des ersten Bildes oder undefined, falls keine vorhanden ist
 */
function pickOverlaySrc(images: GalleryImage[]): string | undefined {
    return images[0]?.overlayUrl;
}

function pickSrc(images: GalleryImage[]): string {
    return (
        images.find((img) => img.size === 'MOBILE')?.url ||
        images.find((img) => img.size === 'TABLET')?.url ||
        images[0]?.url ||
        ''
    );
}

function pickAlt(images: GalleryImage[]): string {
    return images[0]?.title || images[0]?.text || '';
}

function toLeftRenderData(leftImage: GalleryImage[]): ImageRenderData {
    return {
        src: pickSrc(leftImage),
        srcset: buildSrcset(toImageArray(leftImage)),
        alt: pickAlt(leftImage),
    };
}

function toRightRenderData(imageGroup: GalleryImage[], index: number): RightImageRenderData {
    return {
        src: pickSrc(imageGroup),
        srcset: buildSrcset(toImageArray(imageGroup)),
        alt: pickAlt(imageGroup),
        loading: index === 0 ? 'eager' : 'lazy',
    };
}

/**
 * Build render-ready data for all gallery images (used by the overlay).
 * Returns every unique image group as a flat ImageRenderData array in original order.
 * Uses overlayUrl when present (e.g. overlay-image.png), else normal url.
 * @param images - Gallery images from backend (or undefined)
 * @returns All images as render-ready data, or empty array
 */
export function buildAllGalleryRenderData(images: GalleryImage[] | undefined): ImageRenderData[] {
    if (!images?.length) return [];
    const grouped = groupImagesByTitle(images);
    const seen = new Set<string>();
    const result: ImageRenderData[] = [];

    // Iterate through original images to preserve order
    for (const img of images) {
        const key = getImageGroupKey(img);
        if (seen.has(key)) continue; // Skip if we've already added this group
        seen.add(key);

        const group = grouped.get(key);
        if (!group?.length) continue;

        const overlaySrc = pickOverlaySrc(group);
        const aspectRatio = pickAspectRatio(group);
        if (overlaySrc) {
            result.push({
                src: overlaySrc,
                srcset: '',
                alt: pickAlt(group),
                aspectRatio,
            });
        } else {
            result.push({
                src: pickSrc(group),
                srcset: buildSrcset(toImageArray(group)),
                alt: pickAlt(group),
                aspectRatio,
            });
        }
    }

    return result;
}

/**
 * Build render-ready data for the gallery (no logic in template).
 * Returns null if images are missing or first group is empty.
 * Supports 1–2 images with placeholder slots (white gaps); 3+ as before.
 * @param images - Gallery images from backend (or undefined)
 * @returns Render data for left and right images, or null
 */
export function buildGalleryRenderData(images: GalleryImage[] | undefined): GalleryRenderData | null {
    if (!images?.length) return null;
    const grouped = groupImagesByTitle(images);
    const groups = Array.from(grouped.values());
    if (!groups.length) return null;
    const leftImage = groups[0];
    if (!leftImage?.length) return null;

    const imageCount = groups.length;

    if (imageCount === 1) {
        return {
            left: toLeftRenderData(leftImage),
            right: [{ placeholder: true }, { placeholder: true }],
            imageCount: 1,
        };
    }
    if (imageCount === 2) {
        const rightImages = groups.slice(1, 2);
        return {
            left: toLeftRenderData(leftImage),
            right: [
                rightImages[0] ? toRightRenderData(rightImages[0], 0) : { placeholder: true },
                { placeholder: true },
            ],
            imageCount: 2,
        };
    }

    // 3+ Bilder: 4 Bilder wie 3 behandeln (1 links, 2 rechts untereinander), 5+ wie bisher
    const rightCount = groups.length === 4 ? 2 : 4;
    const rightImages = groups.slice(1, 1 + rightCount);
    return {
        left: toLeftRenderData(leftImage),
        right: rightImages
            .filter((imageGroup): imageGroup is GalleryImage[] => Boolean(imageGroup?.length))
            .map((imageGroup, i) => toRightRenderData(imageGroup, i)),
        imageCount,
    };
}
